import { ReactNode } from 'react';
import { currentLocale, t } from '../lib/i18n';
import { setJourneyI18n } from './i18nBridge';
setJourneyI18n({ t, locale: currentLocale });
import { EXIT_REASON, HOLD_REASON, outcomeLabel, outcomeTone, RunLogEntry, STEP_LABEL, StepType } from './model';

const KIND_LABEL: Record<string, string> = {
  entered: 'вход',
  completed: 'шаг выполнен',
  held: 'удержание',
  error: 'ошибка',
  exited: 'выход',
  skipped_entry: 'не вошёл',
  goal: 'цель',
  migrated: 'миграция',
};

function details(d: Record<string, unknown> | null): string {
  if (!d) return '';
  const parts: string[] = [];
  if (typeof d.reason === 'string') parts.push(t(HOLD_REASON[d.reason] ?? EXIT_REASON[d.reason] ?? d.reason));
  if (typeof d.until === 'string') parts.push(t('до {time}', { time: new Date(d.until).toLocaleString(currentLocale(), { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) }));
  if (typeof d.event === 'string') parts.push(d.event);
  if (typeof d.variant === 'string') parts.push(t('вариант {v}', { v: d.variant }));
  if (typeof d.from_version === 'number') parts.push(`v${d.from_version} → v${d.to_version}`);
  if (Array.isArray(d.missing_variables) && d.missing_variables.length) parts.push(t('нет переменных: {v}', { v: d.missing_variables.join(', ') }));
  if (typeof d.code === 'string') parts.push(d.code);
  return parts.join(' · ');
}

/**
 * The run log of one enrollment as a dive (like «Погружение письма»): every step with its time and outcome. The log
 * never holds addresses or attribute values — only keys, ids and reasons (staff read it too).
 */
export function RunLogTimeline({ log, messageLink }: { log: RunLogEntry[]; messageLink?: (id: string) => ReactNode }) {
  if (!log.length) return <div className="small muted">{t('Записей пока нет.')}</div>;
  const t0 = new Date(log[0].at).getTime();
  return (
    <div className="dive drop-dive">
      {log.map((e, i) => {
        const dt = Math.round((new Date(e.at).getTime() - t0) / 60_000);
        return (
          <div key={e.id} className={`dive-item ${e.kind === 'error' ? 'bad' : e.kind === 'held' ? 'warn' : outcomeTone(e.outcome)}`} style={{ animationDelay: `${Math.min(i, 20) * 40}ms` }}>
            <div className="row">
              <span className="t">{outcomeLabel(e.outcome)}</span>
              <span className="spacer" />
              <span className="ms">{i === 0 ? new Date(e.at).toLocaleString(currentLocale(), { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : dt < 60 ? t('+{n} мин', { n: dt }) : dt < 2880 ? t('+{n} ч', { n: (dt / 60).toLocaleString(currentLocale(), { minimumFractionDigits: 1, maximumFractionDigits: 1 }) }) : t('+{n} дн.', { n: (dt / 1440).toLocaleString(currentLocale(), { minimumFractionDigits: 1, maximumFractionDigits: 1 }) })}</span>
            </div>
            <div className="m">
              {t(KIND_LABEL[e.kind] ?? e.kind)}
              {e.step_id && <> · {t('шаг')} <code>{e.step_id}</code>{e.step_type ? ` (${t(STEP_LABEL[e.step_type as StepType] ?? e.step_type)})` : ''}</>}
              {e.version ? ` · v${e.version}` : ''}
              {details(e.detail) && <> · {details(e.detail)}</>}
            </div>
            {e.message_id && <div className="m">{t('письмо')} {messageLink ? messageLink(e.message_id) : <code>{e.message_id}</code>}</div>}
          </div>
        );
      })}
    </div>
  );
}
